/**
 * @namespace ThemeEvents
 * @description A collection of theme-specific events that can be used to trigger and listen for changes anywhere in the theme.
 * @example
 * document.dispatchEvent(new VariantUpdateEvent(variant, sectionId, { html }));
 * document.addEventListener(ThemeEvents.variantUpdate, (e) => { console.log(e.detail.variant) });
 */
export class ThemeEvents {
  /** @static @constant {string} Event triggered when a variant is selected */
  static variantSelected = 'variant:selected';
  /** @static @constant {string} Event triggered when a variant is changed */
  static variantUpdate = 'variant:update';
  /** @static @constant {string} Event triggered when a variant is added to cart */
  static cartAdd = 'cart:add';
  /** @static @constant {string} Event triggered when the cart items or quantities are updated */
  static cartUpdate = 'cart:update';
  /** @static @constant {string} Event triggered when a cart update fails */
  static cartError = 'cart:error';
  // Event triggered when quantity-selector value is changed
  static quantitySelectorUpdate = 'quantity-selector:update';
}

/**
 * Event fired after a variant is updated
 * @extends {Event}
 */
export class VariantUpdateEvent extends Event {
  constructor(variant, sourceId, data) {
    super(ThemeEvents.variantUpdate, { bubbles: true });
    this.detail = {
      variant: variant || null,
      sourceId,
      data: {
        html: data.html,
        productId: data.productId,
        source: data.source ?? null,
      },
    };
  }
}

/**
 * Dispatched when a variant is successfully added to the cart.
 * @extends {Event}
 */
export class CartAddEvent extends Event {
  /**
   * @param {Object} lineItem - The line item returned from /cart/add.js
   * @param {string} sourceId - The id of the element the add action was triggered from
   * @param {Object} [data] - Additional event data
   */
  constructor(lineItem, sourceId, data) {
    super(ThemeEvents.cartAdd, { bubbles: true });
    this.detail = {
      lineItem,
      sourceId,
      data
    };
  }
}

/**
 * Dispatched when the cart is updated (quantity change, removal, etc).
 * @extends {Event}
 */
export class CartUpdateEvent extends Event {
  /**
   * @param {Object} resource - The Shopify cart object
   * @param {string} sourceId - ID of the element that triggered the update
   * @param {Object} [data] - Additional event data
   */
  constructor(resource, sourceId, data) {
    super(ThemeEvents.cartUpdate, { bubbles: true });
    this.detail = {
      resource,
      sourceId,
      data
    };
  }
}

/**
 * Dispatched when a cart operation fails.
 * @extends {Event}
 */
export class CartErrorEvent extends Event {
  /**
   * @param {string} sourceId - The id of the element that triggered the failed operation
   * @param {string} message - Short error message from Shopify
   * @param {string} description - Longer error description from Shopify
   * @param {Object} errors - Full errors object from Shopify response
   */
  constructor(sourceId, message, description, errors) {
    super(ThemeEvents.cartError, { bubbles: true });
    this.detail = {
      sourceId,
      data: {
        message,
        description,
        errors
      }
    };
  }
}
